import { Injectable, OnDestroy } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  ExperiencesService,
  WineSuiteJobStatus,
} from "@services/dws/experiences.service";
import { tr, trParams } from "@util/tr";
import { format } from "date-fns";
import { BehaviorSubject, Observable, Subscription, timer } from "rxjs";
import { map } from "rxjs/operators";

const STORAGE_KEY = "winesuite-jobs-async-tracked";
const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED"]);

export interface TrackedWineSuiteJob {
  jobId: string;
  label: string;
  experienceName: string;
  status: string;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  errorSummary?: string;
  lastUpdatedAt?: string;
}

/**
 * Polls WineSuiteJobsAsync status every 2 minutes, keeps running/completed history,
 * and shows toast + download action when finished.
 */
@Injectable({ providedIn: "root" })
export class WineSuiteJobsAsyncTrackerService implements OnDestroy {
  private readonly pollMs = 120000;
  private tracked = new Map<string, TrackedWineSuiteJob>();
  private notified = new Set<string>();
  private readonly jobsSubject = new BehaviorSubject<TrackedWineSuiteJob[]>([]);

  readonly jobs$: Observable<TrackedWineSuiteJob[]> =
    this.jobsSubject.asObservable();
  readonly runningJobsCount$: Observable<number> = this.jobs$.pipe(
    map(
      (jobs: TrackedWineSuiteJob[]) =>
        jobs.filter(
          (job: TrackedWineSuiteJob) =>
            !TERMINAL_STATUSES.has(job.status || "")
        ).length
    )
  );

  private pollSub?: Subscription;

  constructor(
    private readonly experiencesService: ExperiencesService,
    private readonly snackBar: MatSnackBar
  ) {
    this.restoreFromStorage();
    // Defer until after bootstrap: main.ts sets window["injector"] in bootstrapModule's
    // .then(), while this service can be constructed earlier; resource() needs that injector.
    setTimeout(() => {
      for (const [jobId, job] of this.tracked.entries()) {
        if (TERMINAL_STATUSES.has(job.status || "")) {
          continue;
        }
        this.checkJob(jobId);
      }
      this.startPolling();
    }, 0);
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  /** Register a job returned from experience save (or elsewhere). */
  registerJob(jobId: string, experienceName: string): void {
    if (!jobId) {
      return;
    }
    const name = (experienceName || "").trim();
    this.tracked.set(jobId, {
      jobId,
      experienceName: name,
      label: this.jobLabel(name),
      status: "RUNNING",
      lastUpdatedAt: new Date().toISOString(),
    });
    this.persist();
    this.emitJobs();
    this.startPolling();
    // First status check soon so fast jobs get feedback without waiting 2 min
    this.checkJob(jobId);
  }

  refreshStatuses(): void {
    for (const [jobId, job] of this.tracked.entries()) {
      if (TERMINAL_STATUSES.has(job.status || "")) {
        continue;
      }
      this.checkJob(jobId);
    }
  }

  /** Report files exist only after the job finishes successfully. */
  canDownloadJobReport(job: TrackedWineSuiteJob): boolean {
    return (job.status || "") === "COMPLETED";
  }

  downloadJobReport(jobId: string): void {
    if (!jobId) {
      return;
    }
    this.experiencesService.downloadWineSuiteJobReportCsvText(jobId).subscribe({
      next: (csv) => {
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        this.triggerDownload(
          blob,
          `winesuite-job-${jobId}_${format(new Date(), "yyyyMMddHHmm")}.csv`
        );
      },
      error: () => {
        this.snackBar.open(tr("Could not download job report"), undefined, {
          duration: 4000,
        });
      },
    });
  }

  downloadJobReportExcel(jobId: string): void {
    if (!jobId) {
      return;
    }
    this.experiencesService.downloadWineSuiteJobReportXlsxBlob(jobId).subscribe({
      next: (blob) => {
        const xlsx =
          blob.type && blob.type !== "application/octet-stream"
            ? blob
            : new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              });
        this.triggerDownload(
          xlsx,
          `winesuite-job-${jobId}_${format(new Date(), "yyyyMMddHHmm")}.xlsx`
        );
      },
      error: () => {
        this.snackBar.open(tr("Could not download job report"), undefined, {
          duration: 4000,
        });
      },
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return;
      }
      for (const j of parsed) {
        if (j?.jobId) {
          this.tracked.set(j.jobId, {
            jobId: j.jobId,
            experienceName: j.experienceName || "",
            label: this.jobLabel(j.experienceName || ""),
            status: j.status || "RUNNING",
            requestedAt: j.requestedAt,
            startedAt: j.startedAt,
            completedAt: j.completedAt,
            errorSummary: j.errorSummary,
            lastUpdatedAt: j.lastUpdatedAt,
          });
        }
      }
      this.emitJobs();
    } catch {
      /* ignore */
    }
  }

  private persist(): void {
    const arr: TrackedWineSuiteJob[] = [...this.tracked.values()]
      .sort((a, b) => {
        const ad = new Date(a.lastUpdatedAt || a.completedAt || a.startedAt || 0).getTime();
        const bd = new Date(b.lastUpdatedAt || b.completedAt || b.startedAt || 0).getTime();
        return bd - ad;
      })
      .slice(0, 200);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch {
      /* ignore */
    }
  }

  private startPolling(): void {
    if (this.pollSub || !this.hasRunningJobs()) {
      return;
    }
    this.pollSub = timer(this.pollMs, this.pollMs).subscribe(() => {
      for (const [jobId, job] of this.tracked.entries()) {
        if (TERMINAL_STATUSES.has(job.status || "")) {
          continue;
        }
        this.checkJob(jobId);
      }
    });
  }

  private checkJob(jobId: string): void {
    if (this.notified.has(jobId)) {
      return;
    }
    this.experiencesService.getWineSuiteJobStatus(jobId).subscribe({
      next: (status) => this.handleStatus(jobId, status),
      error: () => {
        /* keep polling */
      },
    });
  }

  private handleStatus(jobId: string, statusResponse: WineSuiteJobStatus): void {
    const status = statusResponse?.status;
    if (!status) {
      return;
    }

    const existing = this.tracked.get(jobId);
    const name = (
      statusResponse.experienceName ??
      existing?.experienceName ??
      ""
    ).trim();
    this.tracked.set(jobId, {
      jobId,
      experienceName: name,
      label: this.jobLabel(name),
      status,
      requestedAt: statusResponse.requestedAt || existing?.requestedAt,
      startedAt: statusResponse.startedAt || existing?.startedAt,
      completedAt: statusResponse.completedAt || existing?.completedAt,
      errorSummary: statusResponse.errorSummary || existing?.errorSummary,
      lastUpdatedAt: new Date().toISOString(),
    });

    this.persist();
    this.emitJobs();
    if (!this.hasRunningJobs()) {
      this.pollSub?.unsubscribe();
      this.pollSub = undefined;
    }

    if (!TERMINAL_STATUSES.has(status) || this.notified.has(jobId)) {
      return;
    }
    this.notified.add(jobId);

    const snackName = name || "—";
    if (status === "COMPLETED") {
      this.snackBar
        .open(
          trParams("Background experience slots update finished for \"{{name}}\"", {
            name: snackName,
          }),
          tr("Download report"),
          { duration: 20000 }
        )
        .onAction()
        .subscribe(() => this.downloadJobReport(jobId));
    } else {
      this.snackBar
        .open(
          trParams("Background experience slots update failed for \"{{name}}\"", {
            name: snackName,
          }),
          tr("Download report"),
          { duration: 20000 }
        )
        .onAction()
        .subscribe(() => this.downloadJobReport(jobId));
    }
  }

  private jobLabel(experienceName: string): string {
    const n = (experienceName || "").trim() || "—";
    return trParams("Update slots of experience \"{{name}}\"", { name: n });
  }

  private hasRunningJobs(): boolean {
    for (const job of this.tracked.values()) {
      if (!TERMINAL_STATUSES.has(job.status || "")) {
        return true;
      }
    }
    return false;
  }

  private emitJobs(): void {
    const jobs = [...this.tracked.values()].sort((a, b) => {
      const ad = new Date(
        a.lastUpdatedAt || a.completedAt || a.startedAt || a.requestedAt || 0
      ).getTime();
      const bd = new Date(
        b.lastUpdatedAt || b.completedAt || b.startedAt || b.requestedAt || 0
      ).getTime();
      return bd - ad;
    });
    this.jobsSubject.next(jobs);
  }
}
